/**
 * HTTP assessment service — gateway `/tax-assessments` endpoints (proxied to
 * taxhive-tax-assessment-service, whose router mounts at
 * `/api/v1/tax-assessments`).
 *
 * Path scheme: the gateway already uses `/assessments` for the GST service, so
 * income-tax (ITR) assessments live under `/tax-assessments` to avoid a
 * collision (see taxhive-api-gateway/app/api/v1/tax_assessment_routes.py).
 *
 * Contract notes (verified against tax_assessment_routes.py):
 *  - Create:     `POST  /tax-assessments`              → TaxAssessmentResponse (201).
 *  - Get one:    `GET   /tax-assessments/{id}`.
 *  - By customer:`GET   /tax-assessments/customer/{customer_id}` → `{ items, total, page, page_size }`.
 *  - Update:     `PUT   /tax-assessments/{id}`         (partial body, TaxAssessmentUpdate).
 *  - Status:     `PATCH /tax-assessments/{id}/status?status=<value>` (status is a QUERY param).
 *  - Calc tax:   `POST  /tax-assessments/calculate-tax` → TaxCalculationResponse (no persistence).
 */

import { apiClient, toServiceError } from '@/services/http/client'
import type {
  AssessmentService,
  AuthorizedAssessmentDetail,
  AuthorizedAssessmentPage,
} from './assessments.service'
import type {
  Assessment,
  CreateAssessmentInput,
  UpdateAssessmentInput,
  AssessmentStatus,
  TaxCalculationInput,
  TaxCalculationResult,
  AssessmentDocument,
  LinkAssessmentDocumentInput,
} from '@/types/assessment.types'
import {
  mapAssessmentFromApi,
  mapAssessmentListFromApi,
  mapCreateAssessmentToApi,
  mapUpdateAssessmentToApi,
  mapTaxCalculationToApi,
  mapTaxCalculationFromApi,
  mapAssessmentDocumentFromApi,
  mapAssessmentDocumentListFromApi,
} from './mappers'

const BASE = '/tax-assessments'
const AUTHORIZED_PAGE_SIZE = 25
const CURSOR_PATTERN = /^[A-Za-z0-9_-]{43}$/
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const AUTHORIZED_STATUSES = new Set([
  'not_started',
  'documents_pending',
  'in_progress',
  'under_review',
  'filed',
  'processed',
  'refund_issued',
  'demand_raised',
])

function mapAuthorizedAssessmentPage(
  raw: unknown,
  expectedClientCustomerId: number
): AuthorizedAssessmentPage {
  if (!raw || typeof raw !== 'object' || !Array.isArray((raw as Record<string, unknown>).items)) {
    throw new Error('Invalid Customer-authorized assessment page')
  }
  const page = raw as { items: unknown[]; next_cursor?: unknown }
  if (
    page.next_cursor !== null &&
    page.next_cursor !== undefined &&
    (typeof page.next_cursor !== 'string' || !CURSOR_PATTERN.test(page.next_cursor))
  ) {
    throw new Error('Invalid Customer-authorized assessment cursor')
  }
  const items = page.items.map((value): Assessment => {
    if (!value || typeof value !== 'object')
      throw new Error('Invalid authorized assessment summary')
    const item = value as Record<string, unknown>
    if (
      typeof item.id !== 'string' ||
      typeof item.source_version_id !== 'string' ||
      !UUID_PATTERN.test(item.source_version_id) ||
      !Number.isSafeInteger(item.customer_id) ||
      Number(item.customer_id) !== expectedClientCustomerId ||
      Number(item.customer_id) < 1 ||
      typeof item.financial_year !== 'string' ||
      typeof item.assessment_year !== 'string' ||
      typeof item.itr_type !== 'string' ||
      !/^ITR-[1-7]$/.test(item.itr_type) ||
      typeof item.status !== 'string' ||
      !AUTHORIZED_STATUSES.has(item.status) ||
      typeof item.due_date !== 'string' ||
      (item.filed_date !== null &&
        item.filed_date !== undefined &&
        typeof item.filed_date !== 'string')
    )
      throw new Error('Invalid authorized assessment summary')
    // Copy only the server's documented allowlist. Never spread raw response fields.
    return {
      id: item.id,
      sourceVersionId: item.source_version_id,
      customerId: String(item.customer_id),
      financialYear: item.financial_year,
      assessmentYear: item.assessment_year,
      itrType: item.itr_type as Assessment['itrType'],
      status: item.status as Assessment['status'],
      dueDate: item.due_date,
      ...(typeof item.filed_date === 'string' ? { filedDate: item.filed_date } : {}),
    }
  })
  return { items, nextCursor: typeof page.next_cursor === 'string' ? page.next_cursor : null }
}

function authorizedPageParams(clientCustomerId: number) {
  if (
    !Number.isSafeInteger(clientCustomerId) ||
    clientCustomerId < 1 ||
    clientCustomerId > 2_147_483_647
  ) {
    throw new Error('A verified client-owned Customer ID is required')
  }
  return {
    source_mode: 'current',
    source_family: 'legacy_assessments',
    client_customer_id: String(clientCustomerId),
    page_size: AUTHORIZED_PAGE_SIZE,
  }
}

function mapAuthorizedAssessmentDetail(
  raw: unknown,
  expected: {
    clientCustomerId: number
    item: Assessment
    customerType: 'individual' | 'business'
    sourceMode: 'current' | 'retained'
  }
): AuthorizedAssessmentDetail {
  if (!raw || typeof raw !== 'object')
    throw new Error('Invalid Customer-authorized assessment detail')
  const value = raw as Record<string, unknown>
  const client = value.client_data
  const fields = client && typeof client === 'object' ? (client as Record<string, unknown>) : null
  const item = expected.item
  if (
    value.id !== item.id ||
    value.source_version_id !== item.sourceVersionId ||
    !UUID_PATTERN.test(String(value.id)) ||
    !Number.isSafeInteger(value.customer_id) ||
    value.customer_id !== expected.clientCustomerId ||
    value.customer_type !== expected.customerType ||
    value.financial_year !== item.financialYear ||
    value.assessment_year !== item.assessmentYear ||
    value.itr_type !== item.itrType ||
    !fields ||
    typeof fields.pan !== 'string' ||
    !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(fields.pan) ||
    typeof fields.tax_regime !== 'string' ||
    !['old', 'new'].includes(fields.tax_regime) ||
    fields.status !== item.status ||
    typeof fields.due_date !== 'string' ||
    value.form_data_status !== 'withheld_pending_schema' ||
    value.category !== item.itrType.toLowerCase().replace('-', '_')
  )
    throw new Error('Invalid Customer-authorized assessment detail')

  const decimal = (field: string): string => {
    const candidate = fields[field]
    if (typeof candidate !== 'string' || !/^-?\d+(?:\.\d+)?$/.test(candidate)) {
      throw new Error('Invalid Customer-authorized assessment detail')
    }
    return candidate
  }
  const work = value.origin_firm_work_product
  let originFirmWorkProduct: AuthorizedAssessmentDetail['originFirmWorkProduct'] = null
  if (expected.sourceMode === 'retained') {
    if (!work || typeof work !== 'object')
      throw new Error('Invalid retained assessment work product')
    const record = work as Record<string, unknown>
    if (typeof record.risk_level !== 'string' || typeof record.work_value !== 'string') {
      throw new Error('Invalid retained assessment work product')
    }
    originFirmWorkProduct = {
      assignedToName: typeof record.assigned_to_name === 'string' ? record.assigned_to_name : null,
      riskLevel: record.risk_level,
      workValue: record.work_value,
    }
  } else if (work !== null) {
    throw new Error('Current assessment detail cannot contain former-firm work product')
  }
  return {
    id: item.id,
    sourceVersionId: item.sourceVersionId!,
    customerId: expected.clientCustomerId,
    customerType: expected.customerType,
    financialYear: item.financialYear,
    assessmentYear: item.assessmentYear,
    itrType: item.itrType,
    clientData: {
      pan: fields.pan,
      taxRegime: fields.tax_regime,
      status: fields.status as Assessment['status'],
      dueDate: fields.due_date,
      grossTotalIncome: decimal('gross_total_income'),
      totalDeductions: decimal('total_deductions'),
      taxableIncome: decimal('taxable_income'),
      totalTaxLiability: decimal('total_tax_liability'),
      totalTaxPaid: decimal('total_tax_paid'),
      taxPayable: decimal('tax_payable'),
      refundAmount: decimal('refund_amount'),
      demandAmount: decimal('demand_amount'),
    },
    formDataStatus: 'withheld_pending_schema',
    originFirmWorkProduct,
  }
}

export function createHttpAssessmentService(): AssessmentService {
  return {
    async listForCustomer(customerId: string): Promise<Assessment[]> {
      try {
        const res = await apiClient.get(`${BASE}/customer/${customerId}`)
        return mapAssessmentListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async listAuthorizedClientOwnedPage(
      clientCustomerId: number,
      options: { sourceMode?: 'current' | 'retained'; cursor?: string } = {}
    ): Promise<AuthorizedAssessmentPage> {
      try {
        const params = {
          ...authorizedPageParams(clientCustomerId),
          source_mode: options.sourceMode ?? 'current',
        }
        let pageCursor = options.cursor
        if (pageCursor !== undefined && !CURSOR_PATTERN.test(pageCursor)) {
          throw new Error('Invalid Customer-authorized assessment cursor')
        }
        if (!pageCursor) {
          const opened = await apiClient.get(`${BASE}/authorized-page/cursor`, { params })
          pageCursor = opened.data?.cursor
          if (typeof pageCursor !== 'string' || !CURSOR_PATTERN.test(pageCursor)) {
            throw new Error('Tax service did not return a valid assessment cursor')
          }
        }
        const res = await apiClient.get(`${BASE}/authorized-page`, {
          params: { ...params, cursor: pageCursor },
        })
        return mapAuthorizedAssessmentPage(res.data, clientCustomerId)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async getAuthorizedClientOwnedDetail(
      clientCustomerId,
      item,
      options
    ): Promise<AuthorizedAssessmentDetail> {
      try {
        const params = authorizedPageParams(clientCustomerId)
        if (!item.sourceVersionId || !UUID_PATTERN.test(item.sourceVersionId)) {
          throw new Error('An exact saved assessment version is required')
        }
        const res = await apiClient.get(
          `${BASE}/authorized-detail/${encodeURIComponent(item.id)}`,
          {
            params: {
              source_version_id: item.sourceVersionId,
              source_mode: options.sourceMode,
              client_customer_id: params.client_customer_id,
              customer_type: options.customerType,
              category: item.itrType.toLowerCase().replace('-', '_'),
              financial_year: item.financialYear,
            },
          }
        )
        return mapAuthorizedAssessmentDetail(res.data, {
          clientCustomerId,
          item,
          customerType: options.customerType,
          sourceMode: options.sourceMode,
        })
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async get(id: string): Promise<Assessment> {
      try {
        const res = await apiClient.get(`${BASE}/${id}`)
        return mapAssessmentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async create(input: CreateAssessmentInput): Promise<Assessment> {
      try {
        const res = await apiClient.post(BASE, mapCreateAssessmentToApi(input))
        return mapAssessmentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async update(id: string, patch: UpdateAssessmentInput): Promise<Assessment> {
      try {
        const res = await apiClient.put(`${BASE}/${id}`, mapUpdateAssessmentToApi(patch))
        return mapAssessmentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async updateStatus(id: string, status: AssessmentStatus): Promise<Assessment> {
      try {
        // Backend takes `status` as a query param on the PATCH /{id}/status route.
        const res = await apiClient.patch(`${BASE}/${id}/status`, null, {
          params: { status },
        })
        return mapAssessmentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async calculateTax(input: TaxCalculationInput): Promise<TaxCalculationResult> {
      try {
        const res = await apiClient.post(`${BASE}/calculate-tax`, mapTaxCalculationToApi(input))
        return mapTaxCalculationFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async listDocuments(assessmentId: string): Promise<AssessmentDocument[]> {
      try {
        const res = await apiClient.get(`${BASE}/${assessmentId}/documents`)
        return mapAssessmentDocumentListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async linkDocument(
      assessmentId: string,
      input: LinkAssessmentDocumentInput
    ): Promise<AssessmentDocument> {
      try {
        const res = await apiClient.post(`${BASE}/${assessmentId}/documents`, {
          assessment_id: assessmentId,
          assessment_type: 'tax',
          document_id: input.documentId,
          category: input.category,
          display_name: input.displayName,
          is_mandatory: input.isMandatory ?? false,
        })
        return mapAssessmentDocumentFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async unlinkDocument(assessmentId: string, documentId: string): Promise<void> {
      try {
        await apiClient.delete(`${BASE}/${assessmentId}/documents/${documentId}`)
      } catch (err) {
        throw toServiceError(err)
      }
    },
  }
}
